import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, reactive, ref, watch } from 'vue'
import EnglishProfileReplaceConfirmDialog from '@/components/yagiprime/EnglishProfileReplaceConfirmDialog.vue'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useAuth } from '@/composables/useAuth'
import { emitter } from '@/plugins/emitter'

type AiEnglishProfileResponse = {
  success?: boolean
  message?: string
  is_prime?: boolean
  usage_count?: number | null
  max_usage_count?: number | null
}

type AxiosErrorLike = {
  response?: {
    status?: number
    data?: { message?: string }
  }
}

type YagiprimePaymentService = {
  useFeature: (
    host: unknown,
    feature: string,
    hooks: {
      onSuccess: () => void
      onAlreadyPremium?: () => void
      onCancel?: () => void
    }
  ) => void
}


export default /*#__PURE__*/_defineComponent({
  __name: 'EnglishProfileAiDialog',
  props: {
    value: { type: Boolean },
    currentValue: { default: '' }
  },
  emits: ["input", "adopt-profile", "update:generating"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void
  (e: 'adopt-profile', text: string): void
  (e: 'update:generating', value: boolean): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: boolean, currentValue: string };





const { vm, varuna, yagiprimePaymentService } = useHostServices()
const { user } = useAuth()
const axiosVm = getCurrentInstance()!.proxy as unknown as {
  $axios: {
    post: (
      url: string,
      data: FormData
    ) => Promise<{ data: AiEnglishProfileResponse }>
  }
}

const paymentService = yagiprimePaymentService as YagiprimePaymentService

const isGenerating = ref(false)
const form = reactive({
  strength: '',
  experienceYears: '' as number | string,
  targetIndustry: '',
  targetPosition: '',
  achievements: ''
})
const generatedText = ref('')
const truncated = ref(false)
const isPrime = ref(false)
const usageCount = ref<number | null>(null)
const maxUsageCount = ref<number | null>(null)
const errorMessage = ref('')
const replaceConfirmVisible = ref(false)

const canGenerate = computed(
  () =>
    !!form.strength &&
    form.experienceYears !== '' &&
    Number(form.experienceYears) >= 0
)

watch(isGenerating, (value) => {
  emit('update:generating', value)
})

watch(
  () => props.value,
  (visible) => {
    if (visible) {
      errorMessage.value = ''
    }
  }
)

function closeDialog() {
  emit('input', false)
}

function resetDialog() {
  generatedText.value = ''
  truncated.value = false
  errorMessage.value = ''
}

function generateProfile() {
  if (!canGenerate.value) return

  paymentService.useFeature(vm, 'english-profile-ai', {
    onSuccess: () => executeGenerate(),
    onAlreadyPremium: () => executeGenerate()
  })
}

function executeGenerate() {
  isGenerating.value = true
  errorMessage.value = ''

  const formData = new FormData()
  formData.append('uuid', user.value?.uid ?? '')
  formData.append('strength', form.strength)
  formData.append('experience_years', String(form.experienceYears))
  formData.append('target_industry', form.targetIndustry || '')
  formData.append('target_position', form.targetPosition || '')
  formData.append('achievements', form.achievements || '')

  axiosVm.$axios
    .post(varuna.endPoints.aiEnglishProfile, formData)
    .then((res) => {
      const data = res.data
      if (data && data.success) {
        generatedText.value = data.message || ''
        isPrime.value = !!data.is_prime
        usageCount.value =
          data.usage_count === undefined ? null : data.usage_count
        maxUsageCount.value =
          data.max_usage_count === undefined ? null : data.max_usage_count
        truncated.value = !isPrime.value && generatedText.value.length >= 95
      } else {
        errorMessage.value = (data && data.message) || 'AI生成に失敗しました'
      }
    })
    .catch((err: AxiosErrorLike) => {
      const status = err.response && err.response.status
      const msg = err.response && err.response.data && err.response.data.message
      if (status === 404) {
        errorMessage.value = msg || 'AIを利用するには会員登録が必要です'
      } else if (status === 400) {
        errorMessage.value = msg || 'AIの使用回数が上限に達しました'
      } else {
        errorMessage.value = msg || 'AI生成中にエラーが発生しました'
      }
    })
    .finally(() => {
      isGenerating.value = false
    })
}

function regenerate() {
  generatedText.value = ''
  generateProfile()
}

function adoptProfile() {
  if (props.currentValue && props.currentValue.trim()) {
    replaceConfirmVisible.value = true
    return
  }
  emit('adopt-profile', generatedText.value)
  closeDialog()
  resetDialog()
}

function confirmReplace() {
  emit('adopt-profile', generatedText.value)
  replaceConfirmVisible.value = false
  closeDialog()
  resetDialog()
}

function appendInsteadOfReplace() {
  const merged =
    (props.currentValue || '').trim() + '\n\n' + generatedText.value
  emit('adopt-profile', merged)
  replaceConfirmVisible.value = false
  closeDialog()
  resetDialog()
}

function openYagiprimeDialog() {
  emitter.emit('open-yagiprime-payment-dialog', 'english-profile-ai')
}

return { __sfc: true,props, emit, vm, varuna, yagiprimePaymentService, user, axiosVm, paymentService, isGenerating, form, generatedText, truncated, isPrime, usageCount, maxUsageCount, errorMessage, replaceConfirmVisible, canGenerate, closeDialog, resetDialog, generateProfile, executeGenerate, regenerate, adoptProfile, confirmReplace, appendInsteadOfReplace, openYagiprimeDialog, EnglishProfileReplaceConfirmDialog }
}

})